import { ActorType, ValveDfu, ValveRelay } from '../enums'
import {
  DeviceMessage,
  ValveActionMessage,
  ValveMessage,
  ValveSensor,
  ValveSensorAggregate,
  ValveSensorField,
  ValveSensorUnit,
  ValveStatusMessage
} from '../types'
import i18next from 'i18next'
import { ValveCommand } from '../enums/ValveCommand'
import { isSpineActionMessage } from './device'

export function formatValveSensor(value: number | null, field: ValveSensorField, unit = true) {
  switch (field) {
    case 'setTemperature':
    case 'temperature':
    case 'attachedTemperature':
    case 'nearbyTemperature':
    case 'inTemperature':
    case 'outTemperature':
    case 'attachedDewPoint':
    case 'nearbyDewPoint':
    case 'inDewPoint':
    case 'outDewPoint':
    case 'deltaTemperatureInOut':
    case 'deltaTemperatureOutIn':
      return formatValveSensorTemperature(value, unit)
    case 'humidity':
    case 'attachedHumidity':
    case 'nearbyHumidity':
    case 'inHumidity':
    case 'outHumidity':
    case 'setPoint':
    case 'relativePosition':
    case 'deltaHumidityInOut':
    case 'deltaHumidityOutIn':
      return formatValveSensorHumidity(value, unit)
    case 'absolutePosition':
      return formatValveSensorAbsolutePosition(value, unit)
    case 'attachedPressure':
    case 'nearbyPressure':
    case 'inPressure':
    case 'outPressure':
    case 'deltaPressureInOut':
    case 'deltaPressureOutIn':
      return formatValveSensorPressure(value, unit)
    case 'rssi':
      return formatValveSensorRssi(value, unit)
    default:
      return value
  }
}

export function getValveSensorValue(sensor: ValveSensor, field: ValveSensorField): number | null {
  switch (field) {
    case 'deltaTemperatureInOut':
      return sensor.inTemperature !== null && sensor.outTemperature !== null
        ? sensor.inTemperature - sensor.outTemperature
        : null
    case 'deltaTemperatureOutIn':
      return sensor.inTemperature !== null && sensor.outTemperature !== null
        ? sensor.outTemperature - sensor.inTemperature
        : null
    case 'deltaHumidityInOut':
      return sensor.inHumidity !== null && sensor.outHumidity !== null
        ? sensor.inHumidity - sensor.outHumidity
        : null
    case 'deltaHumidityOutIn':
      return sensor.inHumidity !== null && sensor.outHumidity !== null
        ? sensor.outHumidity - sensor.inHumidity
        : null
    case 'deltaPressureInOut':
      return sensor.inPressure !== null && sensor.outPressure !== null
        ? (sensor.inPressure - sensor.outPressure) * 100
        : null
    case 'deltaPressureOutIn':
      return sensor.inPressure !== null && sensor.outPressure !== null
        ? (sensor.outPressure - sensor.inPressure) * 100
        : null
    default:
      return sensor[field as keyof ValveSensor] as number | null
  }
}

function makeAggregateKey(field: string, statistic: string): keyof ValveSensorAggregate {
  return `${statistic}${field.charAt(0).toUpperCase()}${field.slice(1)}` as keyof ValveSensorAggregate
}

export function getValveSensorAggregateValue(
  sensor: ValveSensorAggregate,
  field: ValveSensorField,
  statistic: string
): number | null {
  const get = (f: string) => sensor[makeAggregateKey(f, statistic)] as number | null

  switch (field) {
    case 'deltaTemperatureInOut': {
      const i = get('inTemperature'),
        o = get('outTemperature')
      return i !== null && o !== null ? i - o : null
    }
    case 'deltaTemperatureOutIn': {
      const i = get('inTemperature'),
        o = get('outTemperature')
      return i !== null && o !== null ? o - i : null
    }
    case 'deltaHumidityInOut': {
      const i = get('inHumidity'),
        o = get('outHumidity')
      return i !== null && o !== null ? i - o : null
    }
    case 'deltaHumidityOutIn': {
      const i = get('inHumidity'),
        o = get('outHumidity')
      return i !== null && o !== null ? o - i : null
    }
    case 'deltaPressureInOut': {
      const i = get('inPressure'),
        o = get('outPressure')
      return i !== null && o !== null ? (i - o) * 100 : null
    }
    case 'deltaPressureOutIn': {
      const i = get('inPressure'),
        o = get('outPressure')
      return i !== null && o !== null ? (o - i) * 100 : null
    }
    default:
      return get(field)
  }
}

export function formatValveSensorTemperature(value: number | null, unit = true) {
  const formattedValue = value !== null ? parseFloat(value.toFixed(2)) : value

  return !unit ? formattedValue : value !== null ? `${formattedValue}°C` : value
}

export function formatValveSensorHumidity(value: number | null, unit = true) {
  const formattedValue = value !== null ? parseFloat(value.toFixed(2)) : value

  return !unit ? formattedValue : value !== null ? `${formattedValue}%` : value
}

export function formatValveSensorPressure(value: number | null, unit = true) {
  const formattedValue = value !== null ? parseFloat(value.toFixed(2)) : value

  return !unit ? formattedValue : value !== null ? `${formattedValue}HPa` : value
}

export function formatValveSensorAbsolutePosition(value: number | null, unit = true) {
  if (value === null) {
    return null
  }

  const formattedValue = Math.round(value / 100)

  return unit ? `${formattedValue}°` : `${formattedValue}`
}

export function formatValveSensorPosition(value: number | null, unit = true) {
  const formattedValue = value !== null ? Math.round((value / 100) * 10) / 10 : value

  return !unit ? formattedValue : value !== null ? `${formattedValue}%` : value
}

export function formatValveSensorRssi(value: number | null, unit = true) {
  const formattedValue = value !== null ? parseFloat(value.toFixed(2)) : value

  return !unit ? formattedValue : value !== null ? `${formattedValue}dBm` : value
}

export function makeValveSensorAggregateKey(
  field: ValveSensorField,
  statistic: 'min' | 'max' | 'avg'
): keyof ValveSensorAggregate {
  return `${statistic}${field.charAt(0).toUpperCase()}${field.slice(1)}` as keyof ValveSensorAggregate
}

export function isValveMessage(message: DeviceMessage): message is ValveMessage {
  return isValveActionMessage(message) || isValveStatusMessage(message)
}

export function isValveActionMessage(message: DeviceMessage): message is ValveActionMessage {
  return message.kind === 'ValveAction'
}

export function isValveStatusMessage(message: DeviceMessage): message is ValveStatusMessage {
  return message.kind === 'ValveStatus'
}

export function formatValveMessage(message: ValveMessage): string {
  if (isValveActionMessage(message)) {
    return formatValveActionMessage(message)
  } else if (isValveStatusMessage(message)) {
    return formatValveStatusMessage(message)
  } else {
    return i18next.t('unknownValveMessageWasSent', { ns: 'valve' })
  }
}

export function formatValveActionMessage(message: ValveActionMessage): string {
  if (isSetTemperatureAction(message)) {
    return formatSetTemperatureActionMessage(message)
  } else if (isSetSetpointAction(message)) {
    return formatSetSetpointActionMessage(message)
  } else if (isSetMinPositionAction(message)) {
    return formatSetMinPositionActionMessage(message)
  } else if (isSetMaxPositionAction(message)) {
    return formatSetMaxPositionActionMessage(message)
  } else if (isUpdateCycleAction(message)) {
    return formatUpdateCycleActionMessage(message)
  } else if (isPairRuuviTagAction(message)) {
    return formatPairRuuviTagActionMessage(message)
  } else if (isDfuAction(message)) {
    return formatDfuActionMessage(message)
  } else if (isBelimoAction(message)) {
    return formatBelimoActionMessage(message)
  } else if (isRelayAction(message)) {
    return formatRelayActionMessage(message)
  } else if (isRebootAction(message)) {
    return formatRebootActionMessage(message)
  } else if (isGetStatusAction(message)) {
    return formatGetStatusActionMessage(message)
  } else if (isCommandAction(message)) {
    return formatCommandActionMessage(message)
  } else {
    return formatUnknownActionMessage(message)
  }
}

export function formatValveStatusMessage(_: ValveStatusMessage): string {
  return i18next.t('valveStatusWasSent', { ns: 'valve' })
}

function formatGetStatusActionMessage(message: ValveActionMessage) {
  return isValveActionMessageSentByActor(message)
    ? i18next.t('actorRequestedValveStatus', {
        ns: 'valve',
        actor: message.actorName
      })
    : i18next.t('valveStatusWasRequested', { ns: 'valve' })
}

function formatSetTemperatureActionMessage(message: ValveActionMessage) {
  return isValveActionMessageSentByActor(message)
    ? i18next.t('actorSetNewSetTemperature', {
        ns: 'valve',
        actor: message.actorName,
        temperature: message.temp_target
      })
    : i18next.t('newSetTemperatureWasSet', {
        ns: 'valve',
        temperature: message.temp_target
      })
}

function formatSetSetpointActionMessage(message: ValveActionMessage) {
  return isValveActionMessageSentByActor(message)
    ? i18next.t('actorSetNewSetpoint', {
        ns: 'valve',
        actor: message.actorName,
        setpoint: message.setpoint
      })
    : i18next.t('newSetpointWasSet', {
        ns: 'valve',
        setpoint: message.setpoint
      })
}

function formatSetMinPositionActionMessage(message: ValveActionMessage) {
  return isValveActionMessageSentByActor(message)
    ? i18next.t('actorSetNewMinPosition', {
        ns: 'valve',
        actor: message.actorName,
        position: Math.round(message.min_position! / 100)
      })
    : i18next.t('newMinPositionWasSet', {
        ns: 'valve',
        position: Math.round(message.min_position! / 100)
      })
}

function formatSetMaxPositionActionMessage(message: ValveActionMessage) {
  return isValveActionMessageSentByActor(message)
    ? i18next.t('actorSetNewMaxPosition', {
        ns: 'valve',
        actor: message.actorName,
        position: Math.round(message.max_position! / 100)
      })
    : i18next.t('newMaxPositionWasSet', {
        ns: 'valve',
        position: Math.round(message.max_position! / 100)
      })
}

function formatUpdateCycleActionMessage(message: ValveActionMessage) {
  return isValveActionMessageSentByActor(message)
    ? i18next.t('actorSetNewUpdateCycle', {
        ns: 'valve',
        actor: message.actorName,
        seconds: message.update_cycle
      })
    : i18next.t('newUpdateCycleWasSet', {
        ns: 'valve',
        seconds: message.update_cycle
      })
}

function formatPairRuuviTagActionMessage(message: ValveActionMessage) {
  const isSentByActor = isValveActionMessageSentByActor(message)

  const REMOVE_PAIRING = 'FFFFFFFFFFFF'

  if (message.ruuvi_pair && message.ruuvi_pair.length > 0) {
    if (
      message.ruuvi_pair[0].in === REMOVE_PAIRING &&
      message.ruuvi_pair[0].out === REMOVE_PAIRING
    ) {
      return isSentByActor
        ? i18next.t('actorRemovedRuuviTagPairing', { ns: 'valve', actor: message.actorName })
        : i18next.t('ruuviTagPairingWasRemoved', { ns: 'valve' })
    } else {
      return isSentByActor
        ? i18next.t('actorPairedNewRuuviTags', { ns: 'valve', actor: message.actorName })
        : i18next.t('newRuuviTagsWerePaired', { ns: 'valve' })
    }
  }

  return i18next.t('newRuuviTagsWerePaired', { ns: 'valve' })
}

function formatDfuActionMessage(message: ValveActionMessage) {
  const isSentByActor = isValveActionMessageSentByActor(message)

  switch (message.dfu) {
    case ValveDfu.SAME_VERSION:
      return isSentByActor
        ? i18next.t('actorRequestedfirmwareUpdateViaFleetControl', {
            ns: 'valve',
            actor: message.actorName
          })
        : i18next.t('firmwareUpdateViaFleetControlWasRequested', { ns: 'valve' })
    case ValveDfu.SWAP_ENVIRONMENT:
      return isSentByActor
        ? i18next.t('actorRequestedFirmwareUpdateToSwapEnvironment', {
            ns: 'valve',
            actor: message.actorName
          })
        : i18next.t('firmwareUpdateToSwapEnvironmentWasRequested', { ns: 'valve' })

    case ValveDfu.UPDATE_TO_SPINE:
      return isSentByActor
        ? i18next.t('actorRequestedFirmwareUpdateToSpine', {
            ns: 'valve',
            actor: message.actorName
          })
        : i18next.t('firmwareUpdateToSpineWasRequested', { ns: 'valve' })

    default:
      return isSentByActor
        ? i18next.t('actorRequestedFirmwareUpdateInSeconds', {
            ns: 'valve',
            actor: message.actorName,
            seconds: message.dfu
          })
        : i18next.t('firmwareUpdateWasRequested', {
            ns: 'valve',
            seconds: message.dfu
          })
  }
}

function formatRelayActionMessage(message: ValveActionMessage) {
  const isSentByActor = isValveActionMessageSentByActor(message)

  switch (message.relay) {
    case ValveRelay.OFF:
      return isSentByActor
        ? i18next.t('actorTurnedOffRelay', { ns: 'valve', actor: message.actorName })
        : i18next.t('relayWasTurnedOff', { ns: 'valve' })
    case ValveRelay.ON:
    default:
      return isSentByActor
        ? i18next.t('actorTurnedOnRelay', { ns: 'valve', actor: message.actorName })
        : i18next.t('relayWasTurnedOn', { ns: 'valve' })
  }
}

function formatRebootActionMessage(message: ValveActionMessage) {
  return isValveActionMessageSentByActor(message)
    ? i18next.t('actorSentRebootAction', { ns: 'valve', actor: message.actorName })
    : i18next.t('rebootActionWasSent', { ns: 'valve' })
}

function formatBelimoActionMessage(message: ValveActionMessage) {
  return isValveActionMessageSentByActor(message)
    ? i18next.t('actorSentBelimoAction', { ns: 'valve', actor: message.actorName })
    : i18next.t('belimoActionWasSent', { ns: 'valve' })
}

function formatCommandActionMessage(message: ValveActionMessage) {
  const isSentByActor = isValveActionMessageSentByActor(message)

  switch (message.cmd) {
    case ValveCommand.FACTORY_RESET:
      return isSentByActor
        ? i18next.t('actorRequestedFactoryReset', {
            ns: 'valve',
            actor: message.actorName
          })
        : i18next.t('factoryResetWasRequested', { ns: 'valve' })

    case ValveCommand.GET_STATUS:
      return isSentByActor
        ? i18next.t('actorRequestedValveStatus', {
            ns: 'valve',
            actor: message.actorName
          })
        : i18next.t('valveStatusWasRequested', { ns: 'valve' })

    case ValveCommand.REBOOT:
      return isSentByActor
        ? i18next.t('actorRequestedValveReboot', {
            ns: 'valve',
            actor: message.actorName
          })
        : i18next.t('valveRebootWasRequested', { ns: 'valve' })

    default:
      return isSentByActor
        ? i18next.t('actorSentUnknownValveCommand', {
            ns: 'valve',
            actor: message.actorName
          })
        : i18next.t('unknownValveCommandWasSent', { ns: 'valve' })
  }
}

function formatUnknownActionMessage(message: ValveActionMessage) {
  return isValveActionMessageSentByActor(message)
    ? i18next.t('actorSentUnknownValveAction', {
        ns: 'valve',
        actor: message.actorName
      })
    : i18next.t('unknownValveActionWasSent', { ns: 'valve' })
}

function isPairRuuviTagAction(message: ValveActionMessage): boolean {
  return 'ruuvi_pair' in message
}

function isUpdateCycleAction(message: ValveActionMessage): boolean {
  return 'update_cycle' in message
}

function isGetStatusAction(message: ValveActionMessage): boolean {
  return 'get_status' in message
}

function isSetTemperatureAction(message: ValveActionMessage): boolean {
  return 'temp_target' in message
}

function isSetSetpointAction(message: ValveActionMessage): boolean {
  return 'setpoint' in message
}

function isSetMinPositionAction(message: ValveActionMessage): boolean {
  return 'min_position' in message
}

function isSetMaxPositionAction(message: ValveActionMessage): boolean {
  return 'max_position' in message
}

function isBelimoAction(message: ValveActionMessage): boolean {
  return 'write' in message
}

function isDfuAction(message: ValveActionMessage): boolean {
  return 'dfu' in message
}

function isRelayAction(message: ValveActionMessage): boolean {
  return 'relay' in message
}

function isRebootAction(message: ValveActionMessage): boolean {
  return 'reboot' in message
}

function isCommandAction(message: ValveActionMessage): boolean {
  return 'cmd' in message
}

function isValveActionMessageSentByActor(message: ValveActionMessage) {
  return (
    message?.actorName !== undefined && message?.actorName !== null && message?.actorName !== ''
  )
}

/**
 * Get a unit for a given field.
 */
export function getValveSensorUnitByField(field: ValveSensorField): ValveSensorUnit | undefined {
  switch (field) {
    case 'setTemperature':
    case 'temperature':
    case 'attachedTemperature':
    case 'nearbyTemperature':
    case 'inTemperature':
    case 'outTemperature':
    case 'attachedDewPoint':
    case 'nearbyDewPoint':
    case 'inDewPoint':
    case 'outDewPoint':
      return '°C'
    case 'humidity':
    case 'attachedHumidity':
    case 'nearbyHumidity':
    case 'inHumidity':
    case 'outHumidity':
    case 'setPoint':
    case 'relativePosition':
      return '%'
    case 'absolutePosition':
      return '°'
    case 'attachedPressure':
    case 'nearbyPressure':
    case 'inPressure':
    case 'outPressure':
    case 'deltaPressureInOut':
    case 'deltaPressureOutIn':
      return 'hPa'
    case 'deltaTemperatureInOut':
    case 'deltaTemperatureOutIn':
      return '°C'
    case 'deltaHumidityInOut':
    case 'deltaHumidityOutIn':
      return '%'
    default:
      return undefined
  }
}

export function translateValveSensorField(field: ValveSensorField) {
  switch (field) {
    case 'temperature':
      return i18next.t('deviceTemperature', { ns: 'valve' })
    case 'humidity':
      return i18next.t('deviceHumidity', { ns: 'valve' })
    default:
      return i18next.t(field, { ns: 'valve' })
  }
}

export function isValveSensor(
  valveSensor: ValveSensor | ValveSensorAggregate
): valveSensor is ValveSensor {
  return (valveSensor as ValveSensor).time !== undefined
}

export function isValveSensorAggregate(
  valveSensor: ValveSensor | ValveSensorAggregate
): valveSensor is ValveSensorAggregate {
  return (valveSensor as ValveSensorAggregate).timeBucket !== undefined
}
